// Auth state + actions for the admin SPA. A single `@preact/signals` signal
// drives the App gate; the firebase SDK lives behind `./firebase` so this
// module (and its tests) never touch the network.

import { signal } from "@preact/signals";

import { onAuthChange, signInWithGoogle, signOutUser, type FirebaseUser } from "./firebase";

export type AuthStatus = "loading" | "signedOut" | "signedIn" | "forbidden";

export interface AuthState {
  status: AuthStatus;
  user: FirebaseUser | null;
  error: string | null;
}

// Local-dev bypass: when VITE_ADMIN_TOKEN is set the SPA authenticates via the
// X-Admin-Token header (see api/client.ts) and skips firebase completely. Read
// independently of api/client.ts to keep the module graph acyclic.
export const DEV_BYPASS: boolean = (import.meta.env.VITE_ADMIN_TOKEN || "") !== "";

const DEV_PRINCIPAL: FirebaseUser = { uid: "dev-admin", email: "dev@local" };

export const authState = signal<AuthState>(
  DEV_BYPASS
    ? { status: "signedIn", user: DEV_PRINCIPAL, error: null }
    : { status: "loading", user: null, error: null },
);

let started = false;

/**
 * Begin observing auth state. Idempotent — safe to call from both `main.tsx`
 * and an App `useEffect` (tests render <App/> directly). No-op under the dev
 * bypass (state is already `signedIn`).
 */
export function initAuth(): void {
  if (started || DEV_BYPASS) return;
  started = true;
  onAuthChange((user) => {
    if (user) {
      authState.value = { status: "signedIn", user, error: null };
    } else {
      authState.value = { status: "signedOut", user: null, error: null };
    }
  });
}

/** Trigger the Google sign-in popup; surfaces failures as `error`. */
export async function signIn(): Promise<void> {
  authState.value = { ...authState.value, error: null };
  try {
    await signInWithGoogle();
  } catch (err) {
    const message = err instanceof Error ? err.message : "Sign-in failed";
    // Popup-closed is a user action, not an error worth shouting about.
    if (!/popup-closed|cancelled-popup|popup_closed/i.test(message)) {
      authState.value = { ...authState.value, error: message };
    }
  }
}

/** Sign out and return to the login screen. */
export async function signOut(): Promise<void> {
  try {
    await signOutUser();
  } finally {
    authState.value = { status: "signedOut", user: null, error: null };
  }
}

/**
 * Called by the API client on an HTTP 403: the user is authenticated but lacks
 * the `admin:true` claim. Switches the gate to the not-authorized screen.
 */
export function markForbidden(): void {
  if (authState.value.status !== "forbidden") {
    authState.value = { ...authState.value, status: "forbidden" };
  }
}
